import { useMemo, useState } from 'react';
import { PAISES, PAIS_POR_DEFECTO, banderaUrl } from '../lib/paises';
import { Combo, type OpcionCombo } from './Combo';

/**
 * País y teléfono con código de país. La lista salió de Polaris; la forma de
 * buscarla, no: acá **se escribe en el propio campo** (ver `Combo.tsx`).
 */

const Bandera = ({ iso }: { iso: string }) => (
  <img
    src={banderaUrl(iso)}
    alt=""
    loading="lazy"
    className="h-3.5 w-5 shrink-0 rounded-[2px] object-cover"
  />
);

/** Se construye una vez: 243 países × un `<img>` por opción no hace falta rehacerlos. */
function usarOpciones(conPrefijo: boolean): OpcionCombo[] {
  return useMemo(
    () =>
      PAISES.map((p) => ({
        clave: p.iso,
        texto: p.nombre,
        icono: <Bandera iso={p.iso} />,
        // Se puede buscar «57» además de «Colombia»: quien tiene el número
        // delante piensa en el prefijo, no en el nombre.
        alterno: p.dial,
        ...(conPrefijo ? { detalle: p.dial } : {}),
      })),
    [conPrefijo],
  );
}

export function SelectorPais({
  etiqueta,
  iso,
  alCambiar,
}: {
  etiqueta: string;
  iso: string | null;
  alCambiar: (iso: string | null) => void;
}) {
  const opciones = usarOpciones(false);
  return (
    <Combo
      etiqueta={etiqueta}
      opciones={opciones}
      valor={iso}
      alCambiar={(v) => alCambiar(v === null ? null : String(v))}
      vacio="Sin especificar"
      sinCoincidencias="Ningún país coincide."
    />
  );
}

/**
 * Código de país + número. El valor final es `+58 4141234567`.
 *
 * ── Por qué acá el prefijo SÍ es un desplegable aparte ──
 * El campo es el **número de teléfono**: es lo que se escribe. Convertirlo en
 * buscador de países dejaría sin dónde escribir el número. El prefijo es un
 * control angosto al lado, y al abrirlo la lista se busca desde su propio campo,
 * que aparece en el lugar del prefijo.
 */
export function SelectorTelefono({
  etiqueta,
  dial,
  numero,
  alCambiar,
}: {
  etiqueta: string;
  dial: string;
  numero: string;
  alCambiar: (dial: string, numero: string) => void;
}) {
  const opciones = usarOpciones(true);
  const [eligiendoPais, setEligiendoPais] = useState(false);

  // Varios países comparten prefijo (+1 es de EE. UU. y de medio Caribe), así que
  // se muestra el primero que lo tenga: la bandera es indicativa, el prefijo es el dato.
  const actual = PAISES.find((p) => p.dial === dial) ?? PAISES[0]!;

  if (eligiendoPais) {
    return (
      <Combo
        etiqueta={`${etiqueta} — código de país`}
        opciones={opciones}
        valor={actual.iso}
        alCambiar={(clave) => {
          const elegido = PAISES.find((p) => p.iso === clave);
          if (elegido) alCambiar(elegido.dial, numero);
          setEligiendoPais(false);
        }}
        vacio="Buscar país o prefijo…"
        sinCoincidencias="Ningún país coincide."
      />
    );
  }

  return (
    <div className="relative">
      <span className="pointer-events-none absolute -top-2 left-3 z-10 rounded bg-white px-1.5 text-xs font-semibold text-marino-500">
        {etiqueta}
      </span>
      <div
        className="flex w-full items-stretch rounded-2xl border border-marino-200 bg-white
          transition focus-within:border-marino-700 focus-within:ring-4 focus-within:ring-marino-700/10"
      >
        <button
          type="button"
          onClick={() => setEligiendoPais(true)}
          aria-label={`Cambiar el código de país. Ahora: ${actual.nombre}, ${dial}`}
          className="flex shrink-0 items-center gap-1.5 rounded-l-2xl border-r border-marino-100
            px-3 text-sm text-marino-700 transition hover:bg-marino-50"
        >
          <Bandera iso={actual.iso} />
          <span className="tabular-nums">{dial}</span>
        </button>
        <input
          type="tel"
          value={numero}
          // Se aceptan dígitos y espacios: quien copia un número de WhatsApp lo
          // trae con guiones y paréntesis, y limpiarlo acá evita que el campo
          // rechace algo que en realidad está bien.
          onChange={(e) => alCambiar(dial, e.target.value.replace(/[^\d\s]/g, ''))}
          placeholder="414 1234567"
          className="w-full min-w-0 rounded-r-2xl bg-transparent px-3 py-3.5 text-marino-900
            outline-none placeholder:text-marino-400/70"
        />
      </div>
    </div>
  );
}

export { PAIS_POR_DEFECTO };
